import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';

import { Add as CreateIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  Grid,
  IconButton,
  Link as MuiLink,
  Paper,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';

import { Table } from '../../components/table/table';
import { HeadCell, Row, RowCell } from '../../components/table/types';
import { CreateApplicationModal, DeleteApplicationModal, EditApplicationModal } from './components';

import {
  type CreateApplicationDTO,
  useCreateApplicationMutation,
  useDeleteApplicationMutation,
  useGetApplicationsQuery,
  useUpdateApplicationMutation,
} from '../../api';
import { UTCToDateTime } from '../../utils/dates';
import { RouterPaths } from '../../router/paths';
import { useAppActions } from '../../utils/hooks';
import { layoutAction } from '../../store/sideBarStore/layoutStore';
import type { Application } from '../../types';
import { permissionsCheck } from '../../utils/permission';

export const ABFPaymentApps: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const hasPermissionToViewApplicationTokens = permissionsCheck(['READ_APPLICATION_TOKENS']);
  const hasPermissionToEditApplication = permissionsCheck(['WRITE_APPLICATION1S']);

  // api
  const { data: appsList, isLoading: isAppsLoading } = useGetApplicationsQuery();
  const [createApp, { isLoading: isAppCreateLoading }] = useCreateApplicationMutation();
  const [updateApp, { isLoading: isAppUpdateLoading }] = useUpdateApplicationMutation();
  const [deleteApp, { isLoading: isAppDeleteLoading }] = useDeleteApplicationMutation();

  // variables
  const isLoading = isAppsLoading || isAppCreateLoading || isAppUpdateLoading || isAppDeleteLoading;
  const [activeApp, setActiveApp] = useState<Application | null>(null);

  // show action button
  const { setSpeedDialButtonComponent, removeSpeedDialButtonContent } = useAppActions(layoutAction);
  useEffect(() => {
    hasPermissionToEditApplication &&
      setSpeedDialButtonComponent([{ icon: <CreateIcon />, onClick: openCreateModal }]);

    return () => {
      removeSpeedDialButtonContent();
    };
  }, []);

  // fields for forms logic
  const [editFields, setEditFields] = useState<CreateApplicationDTO['body'] | null>(null);
  const handleEditFields = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const name = e.target.name;
    const value = e.target.value;

    setEditFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // create app modal logic
  const [createModalShown, setCreateModalShown] = useState(false);
  const openCreateModal = () => {
    setCreateModalShown(true);
  };
  const closeCreateModal = () => {
    setCreateModalShown(false);
    setEditFields(null);
  };

  // edit app modal logic
  const [editModalShown, setEditModalShown] = useState(false);
  const openEditModal = (app: Application) => {
    setEditModalShown(true);
    setActiveApp(app);
    setEditFields(app);
  };
  const closeEditModal = () => {
    setEditModalShown(false);
    setActiveApp(null);
    setEditFields(null);
  };

  // delete app modal logic
  const [deleteModalShown, setDeleteModalShown] = useState(false);
  const openDeleteModal = (app: Application) => {
    setDeleteModalShown(true);
    setActiveApp(app);
  };
  const closeDeleteModal = () => {
    setDeleteModalShown(false);
    setActiveApp(null);
  };

  // create application handler
  const createApplication = async () => {
    if (!editFields) return;

    const args = {
      body: { name: editFields.name },
    };

    try {
      await createApp(args).unwrap();
      closeCreateModal();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // edit application handler
  const editApplication = async () => {
    if (!activeApp || !editFields) return;

    const args = {
      application_uid: activeApp.uid,
      body: { name: editFields.name },
    };

    try {
      await updateApp(args).unwrap();
      closeEditModal();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // delete application handler
  const deleteApplication = async () => {
    if (!activeApp) return;

    const args = { application_uid: activeApp.uid };

    try {
      await deleteApp(args).unwrap();
      closeDeleteModal();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // table cells and rows
  const headCells: HeadCell[] = [
    { id: '1', label: t('abf-pay-apps.head-cells.uid') },
    { id: '2', label: t('abf-pay-apps.head-cells.name') },
    { id: '3', label: t('abf-pay-apps.head-cells.created-at') },
    {
      id: '4',
      label: t('abf-pay-apps.head-cells.actions'),
      hidden: !hasPermissionToEditApplication,
    },
  ];

  const rows: Row[] | undefined = appsList?.applications.map((it) => {
    const { uid, name, created_at } = it;

    const cells: RowCell[] = [
      {
        name: 'uid',
        component: hasPermissionToViewApplicationTokens ? (
          <MuiLink underline="hover" component={Link} to={`${RouterPaths.Application}/${uid}`}>
            <Typography>{uid}</Typography>
          </MuiLink>
        ) : (
          <>{uid}</>
        ),
        sx: { cursor: 'pointer' },
      },
      { name: 'name', value: name },
      {
        name: 'created_at',
        value: created_at,
        formatting: (value: string) => {
          return UTCToDateTime(value);
        },
      },
      {
        name: 'actions',
        hidden: !hasPermissionToEditApplication,
        component: (
          <Stack direction="row" spacing={2}>
            <IconButton
              aria-label="edit"
              color="warning"
              onClick={() => openEditModal(it)}
              disabled={isLoading}
            >
              <EditIcon />
            </IconButton>

            <IconButton
              aria-label="delete"
              color="error"
              onClick={() => openDeleteModal(it)}
              disabled={isLoading}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ];

    return { cells };
  });

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
        </Paper>
      </Grid>

      <CreateApplicationModal
        open={createModalShown}
        onClose={closeCreateModal}
        onCreate={createApplication}
        fields={editFields}
        fieldsHandler={handleEditFields}
        isLoading={isAppCreateLoading}
      />

      <EditApplicationModal
        open={editModalShown}
        onClose={closeEditModal}
        onEdit={editApplication}
        fields={editFields}
        fieldsHandler={handleEditFields}
        isLoading={isAppUpdateLoading}
      />

      <DeleteApplicationModal
        open={deleteModalShown}
        onClose={closeDeleteModal}
        onDelete={deleteApplication}
        isLoading={isAppDeleteLoading}
      />
    </>
  );
};
